import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { CacheBuster } from 'ts-cacheable';
import { ViewList } from 'src/app/view/model/viewList';
import { ViewEdit } from 'src/app/view/model/viewEdit';
import { MessengerService } from './messenger.service';
import { TranslateService } from '@ngx-translate/core';

const viewsCacheBuster = new Subject<void>();

@Injectable({
  providedIn: 'root'
})
export class ViewService implements OnDestroy
{
  public viewTypes = [
    { name: 'Metric', value: 'METRIC' },
    { name: 'Report', value: 'REPORT' }
  ];

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private messengerService: MessengerService,
    private translate: TranslateService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  /**
   * Delete a View.
   *
   * @param id View id
   */
  @CacheBuster(
    {
      cacheBusterNotifier: viewsCacheBuster
    })
  public deleteView(id: number): Observable<ViewList>
  {
    return this.http.delete<ViewList>(`/view/${id}`);
  }

  /**
   * Save a View.
   *
   * @param data View to persit
   * @param isNew Indicate if this is a new or update
   */
  @CacheBuster(
    {
      cacheBusterNotifier: viewsCacheBuster
    })
  public saveView(data: any, isNew: boolean, viewId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/view', data);
    }
    else
    {
      return this.http.put(`/view/${viewId}`, data);
    }
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get View for grid list.
   */
  public getViewList(): Observable<ViewList[]>
  {
    return this.http.get<ViewList[]>('/views/list');
  }
  
  public getCachedViewNames(viewType: string | 'METRIC' | 'REPORT'): Observable<any[]>
  {
    return this.http.get<any[]>(`/views/all/${viewType}`);    
  }

  /**
   * Get View by id.
   *
   * @param id View id
   */
  public getViewById(id: number): Observable<ViewEdit>
  {
    return this.http.get<ViewEdit>(`/view/${id}`);
  }

  /**
   * Check if View is in use
   *
   * @param id View Id
   */
  public viewUsedInExtract(viewName: string): Observable<any>
  {
    return this.http.get(`/view/inuse/${viewName}`);
  }

  /**
   * Check if View Name exists to avoid name duplicates
   *
   * @param name View Name
   */
  public viewByNameExists(name: string): Observable<any>
  {
    return this.http.get(`/view/exists?name=${name}`);
  }

  // Check if the view is already defined by view_name
  public viewByViewNameExists(viewName: string, id: number): Observable<any>
  {
    return this.http.get(`/view/viewname/exists?viewName=${viewName}&id=${id}`);
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    this.translate.get(['REPORT-TYPE-METRIC', 'REPORT-TYPE-REPORT']).subscribe({
      next: (res) =>
      {
        let index = 0;
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            this.viewTypes[index].name = res[key];
            index++;
          }
        }
      }
    });
  }
}
